import { useState, useRef, useEffect } from 'react';
import { Outlet, Link, useLocation, NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Folder, Settings, Users, Trash2, LogOut, Menu, X, ChevronDown, Home, Upload as UploadIcon, Sparkles } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useUploader } from '../hooks/useUploader';
import { dropdown, pageVariants, spring, snappySpring } from '../lib/motion';
import { MovingBorder } from './aceternity/MovingBorder';
import { FloatingDock } from './aceternity/FloatingDock';
import { GlobeBackground } from './aceternity/GlobeBackground';
import { AnimatedTooltip } from './aceternity/AnimatedTooltip';

const NAV_ACCENTS: Record<string, string> = {
  '/files': 'from-primary to-accent-violet',
  '/trash': 'from-accent-amber to-primary',
  '/admin': 'from-accent-cyan to-accent-violet',
  '/settings': 'from-accent-violet to-accent-cyan',
};

export function Layout() {
  const { user, logout, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const uploader = useUploader();

  const navItems = [
    { path: '/files', label: '我的文件', icon: Home },
    { path: '/trash', label: '回收站', icon: Trash2 },
  ];

  const adminItems = user?.role === 'admin' ? [
    { path: '/admin', label: '管理后台', icon: Users },
  ] : [];

  const allNav = [...navItems, ...adminItems];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as HTMLElement)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);
  useEffect(() => { void refreshUser(); }, [location.pathname, refreshUser]);

  return (
    <div className="flex h-screen bg-transparent overflow-hidden">
      {/* Aceternity GitHub Globe 全局背景 */}
      <div className="pointer-events-none fixed inset-0 z-0 opacity-90" aria-hidden="true">
        <GlobeBackground globeConfig={{ autoRotateSpeed: 0.5, globeColor: '#1a0a12', atmosphereColor: '#ff2e4d' }} />
      </div>
      {/* 侧边栏 */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-bg-secondary/90 lg:bg-bg-secondary/60 backdrop-blur-2xl border-r border-border-soft flex flex-col transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] lg:relative lg:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* 顶部霓虹光带 */}
        <div className="h-0.5 w-full brand-gradient" />
        <div className="flex h-16 items-center justify-between px-4 border-b border-border-soft">
          <Link to="/files" className="flex items-center gap-2.5 font-bold group">
            <motion.span
              whileHover={{ scale: 1.1, rotate: 6 }}
              whileTap={{ scale: 0.94 }}
              transition={snappySpring}
              className="relative flex items-center justify-center w-10 h-10 rounded-xl brand-gradient glow-primary"
            >
              <Folder className="w-5 h-5 text-white" />
              <Sparkles className="w-3 h-3 text-white absolute -top-1 -right-1 animate-float" />
            </motion.span>
            <div className="leading-none">
              <span className="text-lg neon-text font-black tracking-tight">红卫兵网盘</span>
              <span className="block text-[9px] tracking-[0.3em] text-text-muted mt-1">NETDISK · 极繁</span>
            </div>
          </Link>
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-card text-text-muted hover:text-text transition-colors active:scale-90"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="关闭菜单"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-1.5 overflow-y-auto">
          {[...navItems, ...adminItems].map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);
            const accent = NAV_ACCENTS[item.path] || 'from-primary to-accent-violet';
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`group relative flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold transition-colors duration-200 overflow-hidden ${
                  isActive ? 'text-white' : 'text-text-muted hover:text-text'
                }`}
              >
                {isActive && (
                  <motion.span layoutId="nav-active" transition={spring} className={`absolute inset-0 bg-gradient-to-r ${accent} opacity-90 rounded-xl`} />
                )}
                {!isActive && (
                  <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-card/70 rounded-xl" />
                )}
                <motion.span whileHover={{ scale: 1.15, rotate: -6 }} transition={snappySpring} className="relative z-10 flex items-center">
                  <Icon className="w-5 h-5 flex-shrink-0" />
                </motion.span>
                <span className="relative z-10">{item.label}</span>
                <span className={`relative z-10 ml-auto w-1.5 h-1.5 rounded-full bg-white transition-opacity ${isActive ? 'opacity-100 animate-pulse' : 'opacity-0'}`} />
              </NavLink>
            );
          })}
        </nav>

        <div className="p-3 border-t border-border-soft">
          <NavLink
            to="/settings"
            className="group flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold text-text-muted hover:text-text hover:bg-card/70 transition-all duration-200"
          >
            <motion.span whileHover={{ rotate: 90 }} transition={snappySpring}>
              <Settings className="w-5 h-5" />
            </motion.span>
            设置
          </NavLink>
        </div>
      </aside>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            variants={{ hidden: { opacity: 0 }, show: { opacity: 1 }, exit: { opacity: 0 } }}
            initial="hidden" animate="show" exit="exit"
            className="fixed inset-0 z-30 bg-black/70 backdrop-blur-sm lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      <div className="relative z-10 flex-1 flex flex-col min-w-0">
        <header className="h-16 glass border-b border-border-soft flex items-center justify-between gap-2 px-3 sm:px-4 lg:px-6 sticky top-0 z-20 flex-shrink-0">
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-card text-text-muted hover:text-text transition-colors active:scale-90 flex-shrink-0"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="打开菜单"
          >
            <Menu className="w-6 h-6" />
          </button>

          <Link to="/files" className="lg:hidden flex items-center gap-1.5 font-bold flex-shrink-0">
            <span className="flex items-center justify-center w-7 h-7 rounded-lg brand-gradient">
              <Folder className="w-4 h-4 text-white" />
            </span>
            <span className="text-sm neon-text font-black">红卫兵网盘</span>
          </Link>

          <div className="flex-1 hidden lg:block" />

          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <input
              ref={uploadInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.length) uploader.addTasks(e.target.files, null);
                e.target.value = '';
              }}
            />
            <MovingBorder
              borderRadius="0.75rem"
              duration={3200}
              onClick={() => uploadInputRef.current?.click()}
              containerClassName="flex-shrink-0"
              innerClassName="bg-card/95 backdrop-blur-xl"
              className="px-3 sm:px-4 py-2 text-sm font-bold"
            >
              <span className="flex items-center gap-2 text-text">
                <UploadIcon className="w-4 h-4 text-primary" />
                <span className="hidden sm:inline">上传</span>
              </span>
            </MovingBorder>

            <div className="relative" ref={userMenuRef}>
              <AnimatedTooltip content={user?.email || '账户'}>
                <motion.button
                  whileTap={{ scale: 0.94 }}
                  transition={snappySpring}
                  onClick={() => setUserMenuOpen((v) => !v)}
                  className="flex items-center gap-2 px-1.5 py-1.5 rounded-xl hover:bg-card/70 transition-colors"
                  aria-label="账户菜单"
                >
                  <span className="relative w-9 h-9 rounded-full brand-gradient flex items-center justify-center text-white text-sm font-bold ring-2 ring-white/20">
                    {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-success border-2 border-bg-secondary" />
                  </span>
                  <motion.span animate={{ rotate: userMenuOpen ? 180 : 0 }} transition={snappySpring} className="hidden sm:flex">
                    <ChevronDown className="w-4 h-4 text-text-muted" />
                  </motion.span>
                </motion.button>
              </AnimatedTooltip>
              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div
                    variants={dropdown}
                    initial="hidden" animate="show" exit="exit"
                    className="absolute right-0 mt-2 w-60 bg-card/95 backdrop-blur-xl border border-border-soft rounded-2xl shadow-2xl glow-violet py-1.5 z-50 origin-top-right overflow-hidden"
                  >
                    <div className="h-0.5 w-full brand-gradient" />
                    <div className="px-4 py-3 border-b border-border-soft">
                      <p className="text-sm text-text font-bold truncate">{user?.name || '用户'}</p>
                      <p className="text-xs text-text-muted truncate">{user?.email}</p>
                      <span className="inline-block mt-1.5 text-[10px] px-2 py-0.5 rounded-full brand-gradient text-white font-semibold">
                        {user?.role === 'admin' ? '管理员' : '成员'}
                      </span>
                    </div>
                    <Link to="/settings" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-text hover:bg-primary/10 transition-colors">
                      <Settings className="w-4 h-4" /> 设置
                    </Link>
                    <button onClick={() => { setUserMenuOpen(false); logout(); }} className="w-full text-left px-4 py-2.5 text-sm text-danger hover:bg-danger/10 flex items-center gap-2 transition-colors">
                      <LogOut className="w-4 h-4" />
                      退出登录
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-3 sm:p-4 lg:p-6 pb-20 lg:pb-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname.startsWith('/files') ? 'files' : location.pathname}
              variants={pageVariants}
              initial="hidden"
              animate="show"
              exit="exit"
              className="h-full"
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>

        {/* 移动端底部导航（Aceternity FloatingDock） */}
        <div
          className="lg:hidden fixed bottom-3 inset-x-0 z-30 flex justify-center"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="glass rounded-3xl border border-border-soft shadow-2xl">
            <FloatingDock
              items={[
                ...allNav.map((item) => ({
                  id: item.path,
                  label: item.label,
                  icon: item.icon,
                  onClick: () => navigate(item.path),
                  active: location.pathname.startsWith(item.path),
                })),
                {
                  id: 'settings',
                  label: '设置',
                  icon: Settings,
                  onClick: () => navigate('/settings'),
                  active: location.pathname.startsWith('/settings'),
                },
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
